import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Paperclip, Upload, Trash2, X, FileText, Loader2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TOTAL = 20 * 1024 * 1024; // 20 MB

const fmtSize = (bytes) => {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
};

// value: array of selected attachment objects [{id, original_filename, size}]
// onChange: (nextArray) => void
export function AttachmentPicker({ value = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchLibrary = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mailing/attachments`);
      setLibrary(r.data || []);
    } catch {
      toast.error('Erreur de chargement de la bibliothèque');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) fetchLibrary();
  }, [open]);

  const totalSize = value.reduce((sum, f) => sum + (f.size || 0), 0);
  const isSelected = (id) => value.some((f) => f.id === id);

  const toggle = (file) => {
    if (isSelected(file.id)) {
      onChange(value.filter((f) => f.id !== file.id));
    } else {
      onChange([...value, file]);
    }
  };

  const removeChip = (id) => onChange(value.filter((f) => f.id !== id));

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 4 Mo');
      e.target.value = '';
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await axios.post(`${API}/mailing/attachments/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Fichier téléversé');
      await fetchLibrary();
      // auto-select the freshly uploaded file
      onChange([...value, { id: r.data.id, original_filename: r.data.filename, size: r.data.size }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors du téléversement');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce fichier de la bibliothèque ?')) return;
    try {
      await axios.delete(`${API}/mailing/attachments/${id}`);
      setLibrary((prev) => prev.filter((f) => f.id !== id));
      onChange(value.filter((f) => f.id !== id));
      toast.success('Fichier supprimé');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">Pièces jointes</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          data-testid="open-attachments-btn"
        >
          <Paperclip className="h-4 w-4 mr-1" />
          Joindre des fichiers
        </Button>
      </div>

      {/* Selected attachments as chips */}
      {value.length > 0 ? (
        <div className="space-y-1" data-testid="selected-attachments">
          {value.map((f) => (
            <div
              key={f.id}
              className="flex items-center justify-between gap-2 bg-muted/60 border rounded-md px-2 py-1 text-sm"
              data-testid={`attachment-chip-${f.id}`}
            >
              <span className="flex items-center gap-2 truncate">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="truncate">{f.original_filename || f.filename}</span>
                <span className="text-xs text-muted-foreground shrink-0">{fmtSize(f.size || 0)}</span>
              </span>
              <button
                type="button"
                onClick={() => removeChip(f.id)}
                className="text-muted-foreground hover:text-destructive shrink-0"
                data-testid={`remove-attachment-${f.id}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <p className={`text-xs mt-1 ${totalSize > MAX_TOTAL ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
            Total : {fmtSize(totalSize)} / 20 Mo
            {totalSize > MAX_TOTAL && ' — dépasse la limite, l\'envoi échouera'}
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Aucune pièce jointe</p>
      )}

      {/* Library dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bibliothèque de fichiers</DialogTitle>
          </DialogHeader>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Sélectionnez ou téléversez des fichiers (max 4 Mo/fichier)</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="upload-attachment-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
              Téléverser
            </Button>
            <input ref={fileRef} type="file" onChange={handleUpload} className="hidden" data-testid="attachment-file-input" />
          </div>

          <div className="mt-3 space-y-1">
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : library.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Bibliothèque vide. Téléversez un premier fichier.</p>
            ) : (
              library.map((f) => (
                <div
                  key={f.id}
                  className={`flex items-center justify-between gap-2 border rounded-md px-3 py-2 cursor-pointer transition-colors ${isSelected(f.id) ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'}`}
                  onClick={() => toggle(f)}
                  data-testid={`library-file-${f.id}`}
                >
                  <span className="flex items-center gap-2 truncate">
                    <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${isSelected(f.id) ? 'bg-primary border-primary' : 'border-muted-foreground/40'}`}>
                      {isSelected(f.id) && <Check className="h-3 w-3 text-primary-foreground" />}
                    </span>
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="truncate text-sm">{f.original_filename}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{fmtSize(f.size || 0)}</span>
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDelete(f.id); }}
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    data-testid={`delete-library-file-${f.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end mt-4">
            <Button type="button" onClick={() => setOpen(false)} data-testid="attachments-done-btn">
              Terminé ({value.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
